import { Common } from 'src/AppState/Common';
import {
    ApiProviderType,
    CollectionType,
    LegacyCategoriesAndGamesType,
    LegacyCreateCategoryResponseType,
    LegacyDeleteCategoryResponseType,
    LegacyEditCategoryParamsType,
    LegacyEditCategoryResponseType,
} from 'src/domains/casino/utils/adapters/CasinoStoreAdapter/types';
import { openapiCategoriesAndGamesStaffAddGameToCategoryRequest } from 'src/domains/casino/api/generated/categories-and-games/openapi_categories_and_games_staff_add_game_to_category';
import { openapiCategoriesAndGamesStaffChangeCategoriesOrderRequest } from 'src/domains/casino/api/generated/categories-and-games/openapi_categories_and_games_staff_change_categories_order';
import { openapiCategoriesAndGamesStaffCreateCategoryRequest } from 'src/domains/casino/api/generated/categories-and-games/openapi_categories_and_games_staff_create_category';
import { openapiCategoriesAndGamesStaffDeleteCategoryRequest } from 'src/domains/casino/api/generated/categories-and-games/openapi_categories_and_games_staff_delete_category';
import { openapiCategoriesAndGamesStaffEditCategoryRequest } from 'src/domains/casino/api/generated/categories-and-games/openapi_categories_and_games_staff_edit_category';
import { openapiCategoriesAndGamesStaffGetCategoriesAndGamesRequest } from 'src/domains/casino/api/generated/categories-and-games/openapi_categories_and_games_staff_get_categories_and_games';
import {
    openapiCategoriesAndGamesStaffEditGameRequest,
    OpenapiCategoriesAndGamesStaffEditGameResponse200Type,
} from 'src/domains/casino/api/generated/categories-and-games/openapi_categories_and_games_staff_edit_game';
import { LegacyEditGameParamsType } from 'src/domains/casino/state/AppCasino/store/game-store/Game.store';
import { openapiCategoriesAndGamesStaffDeleteGameFromCategoryRequest } from 'src/domains/casino/api/generated/categories-and-games/openapi_categories_and_games_staff_delete_game_from_category';
import { openapiCategoriesAndGamesStaffDeleteGameRequest } from 'src/domains/casino/api/generated/categories-and-games/openapi_categories_and_games_staff_delete_game';

export class LegacyApiProvider implements ApiProviderType {
    public constructor(private readonly common: Common) {}

    public async getCategoriesAndGames(collection: CollectionType): Promise<LegacyCategoriesAndGamesType> {
        return this.common.session.call(openapiCategoriesAndGamesStaffGetCategoriesAndGamesRequest, {
            collection_slug: collection,
        });
    }

    public async createCategory(
        position: number,
        name: string,
        collection: CollectionType
    ): Promise<LegacyCreateCategoryResponseType> {
        return this.common.session.call(openapiCategoriesAndGamesStaffCreateCategoryRequest, {
            requestBody: {
                collection,
                category_details: {
                    active: false,
                    game_ids: [],
                    name,
                },
                position,
            },
        });
    }

    public async removeCategory(
        categoryId: number,
        collection: CollectionType
    ): Promise<LegacyDeleteCategoryResponseType> {
        return this.common.session.call(openapiCategoriesAndGamesStaffDeleteCategoryRequest, {
            requestBody: {
                category_id: categoryId,
                collection,
            },
        });
    }

    public async editCategory(
        categoryId: number,
        categoryDetails: LegacyEditCategoryParamsType['requestBody']['category_details'],
        collection: CollectionType
    ): Promise<LegacyEditCategoryResponseType> {
        return this.common.session.call(openapiCategoriesAndGamesStaffEditCategoryRequest, {
            requestBody: {
                category_id: categoryId,
                category_details: categoryDetails,
                collection,
            },
        });
    }

    public async changeCategoryPosition(
        newOrder: number[],
        _updatedCategory: { categoryId: number; newPosition: number },
        collection: CollectionType
    ): Promise<void> {
        await this.common.session.call(openapiCategoriesAndGamesStaffChangeCategoriesOrderRequest, {
            requestBody: {
                collection,
                category_ids: newOrder,
            },
        });
    }

    public async assignGameToCategory(gameId: number, categoryId: number, position: number): Promise<void> {
        await this.common.session.call(openapiCategoriesAndGamesStaffAddGameToCategoryRequest, {
            requestBody: {
                game_id: gameId,
                category_id: categoryId,
                position,
            },
        });
    }

    public async deleteGameFromCategory(
        gameId: number,
        categoryIds: Array<string>,
        collection: CollectionType
    ): Promise<void> {
        await this.common.session.call(openapiCategoriesAndGamesStaffDeleteGameFromCategoryRequest, {
            requestBody: {
                game_id: gameId,
                category_ids: categoryIds.map((id) => parseInt(id, 10)),
                collection: collection,
            },
        });
    }

    public async deleteLiveGame(gameId: number): Promise<void> {
        await this.common.session.call(openapiCategoriesAndGamesStaffDeleteGameRequest, {
            requestBody: {
                game_id: gameId,
            },
        });
    }

    public async changeGamesOrder(
        categoryId: number,
        categoryDetails: LegacyEditCategoryParamsType['requestBody']['category_details'],
        collection: CollectionType
    ): Promise<void> {
        await this.editCategory(categoryId, categoryDetails, collection);
    }

    public editGame = async (
        game: LegacyEditGameParamsType
    ): Promise<OpenapiCategoriesAndGamesStaffEditGameResponse200Type> => {
        return await this.common.session.call(openapiCategoriesAndGamesStaffEditGameRequest, {
            requestBody: {
                game_details: game.gameDetails,
                game_id: game.gameId,
            },
        });
    };
}
